
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Brain, Target, Users, Zap, Globe, Heart, Calendar, CheckCircle, Clock, Star, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useParallax } from '@/hooks/useParallax';
import DocumentTitle from '@/components/DocumentTitle';

const Roadmap = () => {
  const { user } = useAuth();
  
  // Refs for parallax elements
  const starsRef = useRef<HTMLDivElement>(null);
  const planet1Ref = useRef<HTMLDivElement>(null);
  const planet2Ref = useRef<HTMLDivElement>(null);
  
  // Parallax styles
  const starsStyle = useParallax(starsRef, { speed: 0.05 });
  const planet1Style = useParallax(planet1Ref, { speed: -0.1 });
  const planet2Style = useParallax(planet2Ref, { speed: -0.15 });

  const roadmapItems = [
    {
      quarter: "Q1 2024",
      title: "Foundation & Core Features",
      status: "completed",
      icon: <Brain className="h-6 w-6" />,
      description: "Building the core infrastructure and basic AI capabilities for video processing and analysis.",
      features: [
        "Basic video upload and processing",
        "AI-powered transcription",
        "Speaker identification",
        "Object detection",
        "Simple search functionality"
      ]
    },
    {
      quarter: "Q2 2024",
      title: "Advanced Search & Intelligence",
      status: "completed",
      icon: <Search className="h-6 w-6" />,
      description: "Enhancing search capabilities with semantic understanding and advanced AI models.",
      features: [
        "Semantic search capabilities",
        "LLM-powered query expansion",
        "Advanced object recognition",
        "Multi-language support",
        "API access for developers"
      ]
    },
    {
      quarter: "Q3 2024",
      title: "Collaboration & Workflows",
      status: "in-progress",
      icon: <Users className="h-6 w-6" />,
      description: "Adding team collaboration features and advanced workflow automation capabilities.",
      features: [
        "Team collaboration features",
        "Shared video libraries",
        "Custom workflows",
        "Advanced analytics dashboard",
        "Integration marketplace"
      ]
    },
    {
      quarter: "Q4 2024",
      title: "Enterprise & Scale",
      status: "planned",
      icon: <Globe className="h-6 w-6" />,
      description: "Scaling our platform for enterprise customers with advanced security, compliance, and large-scale video management.",
      features: [
        "Enterprise security features",
        "Custom AI model training",
        "White-label solutions",
        "Advanced compliance tools",
        "Global CDN deployment",
        "Enterprise video library indexing",
        "Multi-tenant architecture"
      ]
    },
    {
      quarter: "Q1 2025",
      title: "AI Innovation & Content Creation",
      status: "planned",
      icon: <Zap className="h-6 w-6" />,
      description: "Pushing the boundaries of AI technology with advanced content creation, editing, and personalized experiences.",
      features: [
        "Real-time video analysis",
        "Predictive content insights",
        "Advanced video editing AI",
        "Multi-modal search",
        "Mobile app development",
        "Personalized sports highlights",
        "Auto-merging multiple camera views",
        "Intelligent video summarization"
      ]
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in-development':
      case 'in-progress':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'planned':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'completed':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'in-development':
      case 'in-progress':
        return 'In Progress';
      case 'planned':
        return 'Planned';
      case 'completed':
        return 'Completed';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-900 to-purple-900 text-white relative overflow-hidden">
      <DocumentTitle 
        title="Product Roadmap - VideoBrain.ai | Future of Video Intelligence"
        description="Explore VideoBrain.ai's development roadmap and upcoming features. See our plans for advanced AI capabilities, collaboration tools, and enterprise solutions."
      />
      
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-md bg-black/20 sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-pink-600 flex items-center justify-center relative">
              <Brain className="h-6 w-6 text-white" />
              <div className="absolute inset-0 rounded-full bg-pink-600 animate-pulse opacity-50"></div>
            </div>
            <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-purple-400">VideoBrain</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/features" className="text-gray-300 hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="text-gray-300 hover:text-white transition-colors">Pricing</Link>
            <Link to="/roadmap" className="text-white font-semibold">Roadmap</Link>
            <Link to="/about" className="text-gray-300 hover:text-white transition-colors">About</Link>
          </nav>
          <div className="flex items-center gap-4">
            {user ? (
              <Button asChild className="bg-pink-600 hover:bg-pink-700">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <Button asChild className="bg-pink-600 hover:bg-pink-700">
                <Link to="/login">Get Started</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Parallax background elements */}
      <div ref={starsRef} className="absolute inset-0 pointer-events-none" style={starsStyle}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIgLz48L3N2Zz4=')]"></div>
        
        {/* Stars */}
        <div className="stars absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <div 
              key={i} 
              className="absolute rounded-full bg-white" 
              style={{
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.7 + 0.3,
                animation: `twinkle ${Math.random() * 5 + 3}s infinite ${Math.random() * 5}s`
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Floating planet */}
      <div 
        ref={planet1Ref}
        className="absolute -right-32 top-1/3 w-64 h-64 rounded-full bg-gradient-to-br from-purple-300/20 to-indigo-600/20 blur-xl"
        style={planet1Style}
      ></div>

      {/* Hero section */}
      <section className="py-20 relative z-10">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-300">
              Product Roadmap
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto">
              Discover what's coming next for VideoBrain. We're constantly innovating to bring you the most powerful video intelligence tools.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 relative z-10">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-pink-500 to-purple-600"></div>
              
              <div className="space-y-12">
                {roadmapItems.map((item, index) => (
                  <div key={index} className="relative flex items-start gap-8">
                    {/* Timeline dot */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white shadow-lg">
                        {item.icon}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-pink-500/50 transition-all duration-300 hover:-translate-y-1">
                      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                        <div>
                          <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-2 text-gray-300">
                              <Calendar className="h-4 w-4" />
                              {item.quarter}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(item.status)}`}>
                              {getStatusText(item.status)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-gray-300 mb-6">{item.description}</p>
                      
                      <div className="space-y-3">
                        <h4 className="font-semibold text-lg">Key Features:</h4>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {item.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-center gap-3">
                              <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                              <span className="text-gray-300">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section className="py-20 bg-black/30 backdrop-blur-sm relative z-10">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-purple-400">
                Have Ideas for Our Roadmap?
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              We'd love to hear your feedback and feature requests. Help us shape the future of video intelligence.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="bg-pink-600 hover:bg-pink-700">
                <Link to="/login">
                  Join Our Community
                </Link>
              </Button>
              <Button size="lg" asChild className="bg-white/10 hover:bg-white/20 border border-white/30 text-white">
                <Link to="mailto:feedback@videobrain.ai">
                  Send Feedback
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/40 backdrop-blur-md py-12 border-t border-white/10">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-6 md:mb-0">
              <div className="rounded-md bg-pink-600 p-1">
                <div className="h-6 w-6 text-white flex items-center justify-center font-bold">VB</div>
              </div>
              <span className="font-semibold text-xl">VideoBrain.ai</span>
            </div>
            <div className="flex gap-6">
              <Link to="/features" className="text-gray-300 hover:text-white transition-colors">Features</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-white transition-colors">Pricing</Link>
              <Link to="/roadmap" className="text-white font-semibold">Roadmap</Link>
              <Link to="/about" className="text-gray-300 hover:text-white transition-colors">About</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS for animations */}
      <style>
      {`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `}
      </style>
    </div>
  );
};

export default Roadmap;
